/**
 * Widget crash reporter — the ONLY error visibility we have on customer
 * sites. Deliberately not a Sentry SDK (~25 KB gz vs the whole-bundle
 * budget); a window-level hook that:
 *
 *   - reports ONLY errors whose stack (or filename) references OUR
 *     bundle — never the host page's own errors (privacy + noise)
 *   - caps at 3 reports per page load, deduped by message
 *   - fire-and-forgets to POST /v1/widget-errors (rate-limited server
 *     side; chat-api forwards into Sentry) and must never throw itself
 */

const MAX_REPORTS = 3;

export function installErrorReporter(opts: {
  apiBaseUrl: string;
  scriptBaseUrl: string;
  agent?: string | null;
}): () => void {
  let sent = 0;
  const seen = new Set<string>();

  const ours = (s: string | null | undefined): boolean =>
    !!s && (s.indexOf(opts.scriptBaseUrl) !== -1 || s.indexOf('/widget/v') !== -1);

  const report = (message: string, stack: string | null, kind: string): void => {
    const key = message.slice(0, 100);
    if (sent >= MAX_REPORTS || seen.has(key)) return;
    seen.add(key);
    sent += 1;
    try {
      void fetch(`${opts.apiBaseUrl}/v1/widget-errors`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        keepalive: true,
        body: JSON.stringify({
          message: message.slice(0, 500),
          stack: (stack ?? '').slice(0, 4000),
          kind,
          pageUrl: location.href.slice(0, 500),
          bundle: opts.scriptBaseUrl,
          agent: opts.agent ?? null,
        }),
      }).catch(() => {});
    } catch {
      // Reporting must never take the page down with it.
    }
  };

  const onError = (ev: ErrorEvent): void => {
    const err = ev.error instanceof Error ? ev.error : null;
    const stack = err?.stack ?? null;
    // No stack (cross-origin scrubbed throws) → attribute by filename.
    if (stack ? ours(stack) : ours(ev.filename)) {
      report(String(ev.message || err?.message || 'unknown error'), stack, 'error');
    }
  };

  const onRejection = (ev: Event): void => {
    const r = (ev as Event & { reason?: { message?: unknown; stack?: unknown } | null }).reason;
    const stack = typeof r?.stack === 'string' ? r.stack : null;
    // Rejections without a stack can't be attributed to us — drop them.
    if (ours(stack)) {
      report(String(r?.message ?? 'unhandled rejection'), stack, 'unhandledrejection');
    }
  };

  addEventListener('error', onError);
  addEventListener('unhandledrejection', onRejection);
  return () => {
    removeEventListener('error', onError);
    removeEventListener('unhandledrejection', onRejection);
  };
}
